import React, { useCallback, useContext, useState } from "react";
import axios from "axios";
import moment from "moment";
import Cropper from "react-easy-crop";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import { GlobalContext } from "../../contexts/GlobalContext";
import { getCroppedImage } from "../../utils/getCroppedImage";
import UserAvatar, { invalidateAvatarCache, PROFILE_PHOTO_DOCUMENT_TYPE } from "../common/UserAvatar";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ProfilePhotoCropDialog = ({ open, onClose, onSaved }) => {
  const { globalData, fetchUserData } = useContext(GlobalContext);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc);
    }
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setErrorMessage("");
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select an image file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage("Image must be smaller than 5 MB.");
      return;
    }

    setErrorMessage("");
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc);
    }
    setImageSrc(URL.createObjectURL(file));
  };

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setErrorMessage("Please select and crop an image first.");
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels);
      const fileName = `profile-${globalData?.userId || "user"}.jpg`;

      const fileInfo = {
        documentType: PROFILE_PHOTO_DOCUMENT_TYPE,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: globalData?.userId,
        fileName,
        fileSize: croppedBlob.size,
        mimeType: "image/jpeg",
      };

      const fileResponse = await axios.post("/api/v1/file", fileInfo);
      const fileId = fileResponse.data.fileId;

      const formData = new FormData();
      formData.append("file", croppedBlob, String(fileId));
      await axios.post("/api/v1/azureblobstorage", formData);

      if (globalData?.profilePhotoFileId) {
        invalidateAvatarCache(globalData.profilePhotoFileId);
      }

      await axios.put("/api/v1/me/profile-photo", { fileId });
      invalidateAvatarCache(fileId);
      await fetchUserData();
      onSaved?.(fileId);
      handleClose();
    } catch (error) {
      console.error("Error saving profile photo:", error);
      setErrorMessage("Could not save profile photo. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="profile-photo-dialog-title">
      <DialogTitle id="profile-photo-dialog-title">Profile photo</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {!imageSrc && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <UserAvatar
              profilePhotoFileId={globalData?.profilePhotoFileId}
              displayName={globalData?.displayName}
              size={96}
              sx={{ mx: "auto", mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a photo and adjust zoom and position.
            </Typography>
            <Button variant="contained" component="label">
              Choose image
              <input hidden type="file" accept="image/*" onChange={handleFileChange} />
            </Button>
          </Box>
        )}

        {imageSrc && (
          <Box>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 320,
                bgcolor: "grey.900",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Drag to reposition. Use the slider to zoom.
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value)}
              sx={{ mt: 2 }}
              aria-label="Zoom"
            />
            <Button size="small" component="label" sx={{ mt: 1 }}>
              Choose different image
              <input hidden type="file" accept="image/*" onChange={handleFileChange} />
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || !imageSrc}>
          {isSaving ? <CircularProgress size={22} /> : "Save photo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePhotoCropDialog;
